import { useEffect, useMemo, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import ReactTooltip from "react-tooltip";
import Breadcrumbs from "../../components/Breadcrumbs";
import Header from "../../components/Header";
import PageContainer from "../../components/PageContainer";
import FirmFlowTable from "../../components/mytasks/FirmFlowTable";
import Sidebar from "../../components/Sidebar";
import { cloneService } from "../../services/cloneService";
import { getServices } from "../../services/getServices";
import { DeleteService } from "../../services/service/deleteService";
import CloneServiceModal from "./CloneServiceModal";
import DeleteServiceModal from "./DeleteServiceModal";
// ✅ Add this import (adjust the path if needed)
import { PencilIcon, ClipboardDocumentIcon } from "@heroicons/react/24/solid";
import DepartmentBadge from "../../components/deparments/DepartmentBadge";
import { getServiceDepartments } from "../../services/service/getServiceDepartments";
import { usePermissions } from "../../context/PermissionsContext";

function AllServices() {
    const { can } = usePermissions();
    const navigate = useNavigate();
    const [serviceIdToBeDeleted, setServiceIdToBeDeleted] = useState("");
    const [selectedServiceTemplate, setSelectedServiceTemplate] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
    const [serviceToClone, setServiceToClone] = useState(null);
    const [isCloning, setIsCloning] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [list, setList] = useState([]);

    // ✅ Department filter
    const [departments, setDepartments] = useState([]);
    const [selectedDepartmentId, setSelectedDepartmentId] = useState("__all__");
    const noPermissionMessage = "You don't have permission to do this.";
    const canReadDepartments =
        can("departments.read") ||
        can("departments.write") ||
        can("departments.delete");
    const canReadServices =
        can("services.read") || can("services.write") || can("services.delete");
    const canWriteServices = can("services.write");
    const canDeleteServices = can("services.delete");

    useEffect(() => {
        if (!canReadDepartments || !canReadServices) {
            setLoading(false);
            return;
        }

        callApi();
        callDepartments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canReadDepartments, canReadServices]);

    const callApi = async () => {
        const res = await getServices();
        if (!res?.data) {
            setLoading(false);
            return;
        }
        setList(res.data);
        setLoading(false);
    };

    const onChangeSelectedDepartment = (opt) => {
        if (!opt) {
            setSelectedDepartmentId("__all__");
            return;
        }
        setSelectedDepartmentId(opt.value);
    };

    const callDepartments = async () => {
        const res = await getServiceDepartments();
        if (!res?.success) {
            setDepartments([]);
            return;
        }

        const raw = res?.data?.departments || [];
        const options = raw.map((d) => ({
            value: d.id,
            label: d.name,
        }));

        setDepartments(options);

        // Optional: if you want to auto-select first department instead of "All"
        // if (options.length > 0) setSelectedDepartmentId(options[0].value);
    };

    const getServiceVersionMeta = (s) => {
        const isCurrent = s?.isCurrent ?? s?.IsCurrent ?? true;
        const versionNumber = s?.versionNumber ?? s?.VersionNumber ?? 1;
        const supersededByServiceId =
            s?.supersededByServiceId ?? s?.SupersededByServiceId ?? null;
        return { isCurrent, versionNumber, supersededByServiceId };
    };

    const handleOpenCloneModal = (template) => {
        if (!canWriteServices) {
            toast.error("You don't have permission to do this");
            return;
        }

        setServiceToClone(template);
        setIsCloneModalOpen(true);
    };

    const onCloneService = async () => {
        if (!canWriteServices) {
            toast.error("You don't have permission to do this");
            return;
        }

        if (!serviceToClone?.id || isCloning) return;

        setIsCloning(true);
        try {
            const res = await cloneService(serviceToClone.id);

            if (res?.success && (res?.data?.success ?? true)) {
                toast.success(
                    res?.data?.message || "Service cloned successfully",
                );
                setIsCloneModalOpen(false);
                setServiceToClone(null);

                const clonedServiceId =
                    res?.clonedServiceId ||
                    res?.data?.clonedServiceId ||
                    res?.data?.serviceId;

                if (clonedServiceId) {
                    navigate(`/services/${clonedServiceId}`);
                } else {
                    toast.error(
                        "Service cloned, but the cloned service id was not returned.",
                    );
                    await callApi();
                }
            } else {
                toast.error(
                    res?.data?.message ||
                        res?.message ||
                        "Something went wrong",
                );
            }
        } catch (err) {
            toast.error(err?.message || "Something went wrong");
        } finally {
            setIsCloning(false);
        }
    };

    const handleSetCloneModalOpen = (open) => {
        setIsCloneModalOpen(open);
        if (!open) setServiceToClone(null);
    };

    const handleOpenDeleteModal = (template) => {
        if (!canDeleteServices) {
            toast.error("You don't have permission to do this");
            return;
        }
        setSelectedServiceTemplate(template);
        setIsModalOpen(true);
        setServiceIdToBeDeleted(template?.id);
    };

    const onRemoveService = async (serviceId = serviceIdToBeDeleted) => {
        const response = await DeleteService(serviceId);
        if (response?.success) {
            toast.success(response?.data?.message || "Deleted");
            callApi();
        } else {
            toast.error(response?.data?.message || "Something went wrong");
        }
        setIsModalOpen(false);
    };

    const filteredList = useMemo(() => {
        const base =
            selectedDepartmentId === "__all__"
                ? list || []
                : (list || []).filter((s) => {
                      const dept = s?.department || s?.Department;
                      const deptId = String(
                          dept?.id ?? dept?.Id ?? s?.departmentId ?? "",
                      );
                      return deptId === String(selectedDepartmentId);
                  });

        return base.filter((s) => getServiceVersionMeta(s).isCurrent);
    }, [list, selectedDepartmentId]);

    return (
        <section>
            <Toaster />
            <DeleteServiceModal
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                onRemoveService={() =>
                    onRemoveService(selectedServiceTemplate?.id)
                }
                serviceTemplate={selectedServiceTemplate}
            />
            <CloneServiceModal
                isModalOpen={isCloneModalOpen}
                setIsModalOpen={handleSetCloneModalOpen}
                onCloneService={onCloneService}
                serviceTemplate={serviceToClone}
                isCloning={isCloning}
            />

            <div className="flex h-screen overflow-hidden">
                <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

                <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                    <Header
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />

                    <PageContainer
                        isLoading={loading}
                        loadingText="Loading services..."
                    >
                        <main>
                            <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                                <Breadcrumbs
                                    home="/departments"
                                    pages={[
                                        {
                                            name: "Services",
                                            href: `#`,
                                            current: true,
                                        },
                                    ]}
                                />

                                <div className="mb-8 sm:flex sm:justify-between sm:items-center">
                                    <div className="mb-4 sm:mb-0">
                                        <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                                            Services
                                        </h1>
                                    </div>

                                    <div className="grid items-end justify-start grid-flow-col gap-2 sm:auto-cols-max sm:justify-end">
                                        {/* ✅ Department filter (top-right) */}
                                        <div className="flex flex-col items-start">
                                            <h2 className="font-bold text-1xl md:text-1xl text-slate-800">
                                                Select Department
                                            </h2>

                                            <Select
                                                onChange={onChangeSelectedDepartment}
                                                className="basic-single min-w-[180px] w-full"
                                                classNamePrefix="select"
                                                isSearchable={true}
                                                // Default to "All Departments" visually
                                                value={
                                                    selectedDepartmentId ===
                                                    "__all__"
                                                        ? {
                                                              value: "__all__",
                                                              label: "All Departments",
                                                          }
                                                        : departments.find(
                                                              (d) =>
                                                                  d.value ===
                                                                  selectedDepartmentId,
                                                          ) || {
                                                              value: "__all__",
                                                              label: "All Departments",
                                                          }
                                                }
                                                options={[
                                                    {
                                                        value: "__all__",
                                                        label: "All Departments",
                                                    },
                                                    ...departments,
                                                ]}
                                                name="departments"
                                            />
                                        </div>
                                        <span
                                            data-tip={
                                                canWriteServices
                                                    ? "Add Service"
                                                    : noPermissionMessage
                                            }
                                            className="inline-flex"
                                        >
                                            <button
                                                onClick={() =>
                                                    navigate(
                                                        "/templates/services/create",
                                                    )
                                                }
                                                disabled={!canWriteServices}
                                                className={`h-10 text-white bg-teal-500 btn ${
                                                    canWriteServices
                                                        ? "hover:bg-teal-600"
                                                        : "opacity-50 cursor-not-allowed"
                                                }`}
                                            >
                                                <svg
                                                    className="w-4 h-4 opacity-50 fill-current shrink-0"
                                                    viewBox="0 0 16 16"
                                                >
                                                    <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                                                </svg>
                                                <span className="ml-2 xs:block">
                                                    Add Service
                                                </span>
                                            </button>
                                        </span>
                                    </div>
                                </div>

                                {canReadDepartments && canReadServices ? (
                                    <FirmFlowTable
                                        columns={[
                                            {
                                                Header: "Name",
                                                accessor: "name",
                                                Cell: (props) => (
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <button
                                                            onClick={() =>
                                                                navigate(
                                                                    `/services/${props.row.original.id}`,
                                                                )
                                                            }
                                                            className="rounded-full text-gray hover:text-blue-500"
                                                        >
                                                            {props.row.original.name}
                                                        </button>
                                                        {(() => {
                                                            const {
                                                                isCurrent,
                                                                versionNumber,
                                                                supersededByServiceId,
                                                            } =
                                                                getServiceVersionMeta(
                                                                    props.row
                                                                        .original,
                                                                );
                                                            return (
                                                                <>
                                                                    {!isCurrent ? (
                                                                        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                                                                            Legacy v
                                                                            {
                                                                                versionNumber
                                                                            }
                                                                        </span>
                                                                    ) : null}
                                                                    {!isCurrent &&
                                                                    supersededByServiceId ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() =>
                                                                                navigate(
                                                                                    `/services/${supersededByServiceId}`,
                                                                                )
                                                                            }
                                                                            className="text-xs text-blue-600 hover:text-blue-700"
                                                                        >
                                                                            Open
                                                                            current
                                                                        </button>
                                                                    ) : null}
                                                                </>
                                                            );
                                                        })()}
                                                    </div>
                                                ),
                                            },
                                            {
                                                Header: "Department",
                                                accessor: "department.name",
                                                Cell: (props) => {
                                                    // Use the full department object so DepartmentBadge can read colorHex, etc.
                                                    const department =
                                                        props?.row?.original
                                                            ?.department;

                                                    return (
                                                        <a
                                                            rel="noreferrer"
                                                            href={`/services/${props.row.original.id}`}
                                                            className="rounded-full text-gray hover:text-blue-500"
                                                        >
                                                            <DepartmentBadge
                                                                name={
                                                                    department.name
                                                                }
                                                                colorHex={
                                                                    department?.colorHex
                                                                }
                                                            />
                                                        </a>
                                                    );
                                                },
                                            },
                                            {
                                                Header: "Actions",
                                                accessor: "id",
                                                Cell: (props) => (
                                                    <td className="w-px px-2 py-3 first:pl-5 last:pr-5 whitespace-nowrap">
                                                        <ReactTooltip
                                                            type="light"
                                                            effect="solid"
                                                            place="top"
                                                            border={true}
                                                            borderColor={
                                                                "rgb(220,220,220)"
                                                            }
                                                            className="rounded-md bg-gray-50"
                                                            multiline={false}
                                                        />

                                                        <button
                                                            data-tip="Edit"
                                                            disabled={
                                                                !canWriteServices
                                                            }
                                                            onClick={() =>
                                                                navigate(
                                                                    `/services/${props.value}`,
                                                                )
                                                            }
                                                            className={`m-4 rounded-full ${
                                                                canWriteServices
                                                                    ? "text-slate-400 hover:text-slate-500"
                                                                    : "text-slate-300 cursor-not-allowed"
                                                            }`}
                                                        >
                                                            <span className="sr-only">
                                                                Menu
                                                            </span>
                                                            <PencilIcon className="w-5 h-5 p-0" />
                                                        </button>

                                                        <button
                                                            data-tip="Duplicate"
                                                            disabled={
                                                                !canWriteServices
                                                            }
                                                            onClick={() =>
                                                                handleOpenCloneModal(
                                                                    props.row
                                                                        .original,
                                                                )
                                                            }
                                                            className={`m-4 rounded-full ${
                                                                canWriteServices
                                                                    ? "text-slate-400 hover:text-slate-500"
                                                                    : "text-slate-300 cursor-not-allowed"
                                                            }`}
                                                        >
                                                            <ClipboardDocumentIcon className="w-5 h-5" />
                                                        </button>

                                                        <button
                                                            data-tip="Delete"
                                                            disabled={
                                                                !canDeleteServices
                                                            }
                                                            onClick={() =>
                                                                handleOpenDeleteModal(
                                                                    props.row
                                                                        .original,
                                                                )
                                                            }
                                                            className={`m-4 rounded-full ${
                                                                canDeleteServices
                                                                    ? "text-slate-400 hover:text-slate-500"
                                                                    : "text-slate-300 cursor-not-allowed"
                                                            }`}
                                                        >
                                                            <svg
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                viewBox="0 0 24 24"
                                                                fill="currentColor"
                                                                className="p-1 pt-0 w-7 h-7 text-slate-400 hover:text-red-500"
                                                            >
                                                                <path
                                                                    fillRule="evenodd"
                                                                    d="M16.5 4.478v.227a48.816 48.816 0 013.878.512.75.75 0 11-.256 1.478l-.209-.035-1.005 13.07a3 3 0 01-2.991 2.77H8.084a3 3 0 01-2.991-2.77L4.087 6.66l-.209.035a.75.75 0 01-.256-1.478A48.567 48.567 0 017.5 4.705v-.227c0-1.564 1.213-2.9 2.816-2.951a52.662 52.662 0 013.369 0c1.603.051 2.815 1.387 2.815 2.951zm-6.136-1.452a51.196 51.196 0 013.273 0C14.39 3.05 15 3.684 15 4.478v.113a49.488 49.488 0 00-6 0v-.113c0-.794.609-1.428 1.364-1.452zm-.355 5.945a.75.75 0 10-1.5.058l.347 9a.75.75 0 101.499-.058l-.346-9zm5.48.058a.75.75 0 10-1.498-.058l-.347 9a.75.75 0 001.5.058l.345-9z"
                                                                    clipRule="evenodd"
                                                                />
                                                            </svg>
                                                        </button>
                                                    </td>
                                                ),
                                            },
                                        ]}
                                        // ✅ filtered data
                                        data={filteredList}
                                    />
                                ) : canReadDepartments ? (
                                    <div className="p-4 text-sm border rounded-sm border-slate-200 text-slate-600">
                                        You don't have permission to view services.
                                    </div>
                                ) : (
                                    <div className="p-4 text-sm border rounded-sm border-slate-200 text-slate-600">
                                        You don't have permission to view
                                        departments.
                                    </div>
                                )}
                            </div>
                        </main>
                    </PageContainer>
                </div>
            </div>
        </section>
    );
}

export default AllServices;
